import React from 'react';
import LetterboxContainer from '@components/letterbox-container/letterbox-container';
import RichText from '@components/rich-text/rich-text';
import styled from '@emotion/styled/macro';
import vars from '@variables';
import { AboutUsBlockProps } from './about-us-block.types';
import { GatsbyImage, getImage } from 'gatsby-plugin-image';
import { GTM_ID } from '../../../../common/constants/gtmIds';
import { H2Atom, PAtom } from '@atoms/text-atoms/text-atoms';
import { LetterboxLinkAtom } from '@atoms/link-atoms/link-atoms';

// @TODO this component currently uses a png for the background image in future iterations this
// should be an svg for scalablity

const AboutUsBlock: React.FC<AboutUsBlockProps> = ({
  backgroundImageSrc,
  title,
  cta = {},
  gatsbyImageData = {},
  image,
  letterboxLinks,
  text = {},
  className,
}) => {
  const gatsbyImage = getImage(gatsbyImageData);
  if (title && text && letterboxLinks) {
    return (
      <div data-testid="about-us-block" className={className}>
        <H2Atom>{title}</H2Atom>
        <div className="content">
          <div className="left-container">
            {gatsbyImage && (
              <GatsbyImage
                image={gatsbyImage}
                alt={image.alt}
                className="image-border"
              />
            )}
            <div className="text-container">
              <RichText content={text} />
              {cta.url && cta.text && (
                <LetterboxLinkAtom url={cta.url} gtmId={GTM_ID.ABOUT_US_CTA}>
                  {cta.text}
                </LetterboxLinkAtom>
              )}
            </div>
          </div>
          <div className="right-container">
            <LetterboxContainer
              links={letterboxLinks}
              linksGtmId={GTM_ID.ABOUT_US_LETTERBOX_LINK}
            />
          </div>
        </div>
      </div>
    );
  } else {
    console.warn(`About Us Block Component called with null data`);
    return null;
  }
};

const StyledAboutUsBlock = styled(AboutUsBlock)`
  width: 100%;
  max-width: ${vars.contentContained};
  margin: 0 auto;
  border: solid ${vars.transparentBorderGrey} 1px;
  background: ${({ backgroundImageSrc }) =>
    `url(${backgroundImageSrc}?fm=webp) no-repeat`};
  padding: ${vars.spacingM};

  ${H2Atom} {
    padding-top: ${vars.spacingL};
    ${vars.fontSize10}
    font-weight: ${vars.fontWeightBold};
    margin-bottom: 1.2rem;
    max-width: calc(0.6 * ${vars.contentMaxWidth});
  }

  .content {
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;
    align-items: top;

    .left-container {
      margin-bottom: ${vars.spacingL};
      display: flex;
      justify-content: flex-end;
      background: transparent;
      @media screen and (min-width: ${vars.breakPointTenColumn}) {
        justify-content: flex-start;
      }

      .image-border img {
        padding-bottom: ${vars.spacingS};
        border-bottom: solid ${vars.emfBlue} 6px;
        max-height: 28rem;
        @media screen and (min-width: ${vars.breakPointTablet}) {
          max-height: 33rem;
        }
      }

      .text-container {
        max-width: 29rem;
        min-width: 16rem;
        display: flex;
        flex-direction: column;
        margin-left: ${vars.spacingL};
        ${RichText} {
          background: transparent;
          margin: 0;
          padding-bottom: ${vars.spacingL};
          ${PAtom} {
            ${vars.fontSize3}
          }
        }
        ${LetterboxLinkAtom} {
          width: 17.1rem;
          min-width: 0;
          border: solid ${vars.borderGrey} 1px;
          ${vars.fontSize3}
          &:hover {
            background-color: ${vars.coreDarkGrey};
            color: ${vars.white};
          }
        }
      }
    }

    .right-container {
      display: none;
      margin-left: ${vars.spacingL};
      @media screen and (min-width: 1010px) {
        display: block;
      }
      ${LetterboxContainer} {
        border: solid ${vars.transparentBorderGrey} 1px;
        width: 100%;
        ${LetterboxLinkAtom} {
          height: 6.2rem;
          ${vars.fontSize5}
          padding: 2.1rem ${vars.spacingM} ${vars.spacingM} ${vars.spacingM};
        }
      }
    }
  }
`;

export default StyledAboutUsBlock;
